import { memo, useEffect, useState } from 'react';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import { CONVERSATIONS, DRAFTS } from 'utils/config/constants/controllerNames';
import { addFirestoreDocs, auth, onUpdateDoc } from 'utils/config/firebase';
import {
    closeRightSidebarAutomatically,
    onOpenEditorFromOutput,
    setEditorSelectedOutput,
    updateConversationGroup,
} from 'store/global/globalSlice';

import FullScreenModal from 'components/Modals/FullScreenModal';
import EditorWithFooter from 'components/Common/EditorWithFooter';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ContextMenuType, CopyMenuType, OutputType } from './types';
import 'animate.css';

import RegenerateBtn from './components/RegenerateBrn/RegenerateBtn';
import { minifyHtml } from 'helpers/functions';
import TopActions from './components/TopActions/TopActions';
import { errorLogger } from 'utils/config/firebase/logs';
import Actions from './components/Actions/Actions';
import { useTranslation } from 'react-i18next';
import Body from './components/Body/Body';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { BrandType } from 'components/DefaultSettings/types';
import { getBrands } from 'api/queries/languages';
import { Message } from 'components/JobGenerator/types';
import { findGroup } from 'components/JobGenerator/groupUtils';
import { getContentValue, R_ONE_FIELDS } from './utils';
import SectionsOutput from './components/SectionsOutput';
import { useParams } from 'react-router-dom';
import { BiasOverlay } from './components/BiasOverlay/BiasOverlay';
import ContextMenu from 'components/Common/ContextMenu';
import CopyView from 'components/Common/CopyView';
import { RewriteModal } from 'components/Modals/RewriteModal';
import type { SectionRewriteData } from 'components/Output/components/SectionsOutput';

function Output({
    isFullScreenMode = false,
    hasActionBtns = true,
    isEmpty = false,
    regenerate,
    messages,
    streamingValue,
    isGeneratingJob,
    stopGenerating,
    onCloseParentFullScreen,
    hiddenMessages = [],
    messagesLength = 0,
    isGeneratingForThis,
    item,
    title,
    isDoneGeneratingForThis,
    scrollToGroup,
    handleRewriteFragment,
}: OutputType) {
    const {
        country,
        version,
        language,
        created_at,
        isHidden,
        isLiked,
        isBookmarked,
        message_id: messageId,
        parameters,
        brand,
        r1_flag,
        has_bias,
        bias_reason,
        is_bias_acknowledged,
    } = item;
    const { document_id } = useParams();
    const [contextMenu, setContextMenu] = useState<ContextMenuType>({
        x: 0,
        y: 0,
        value: '',
        markedHtml: '',
        show: false,
    });

    const [copyMenu, setCopyMenu] = useState<CopyMenuType>({
        x: 0,
        y: 0,
        value: '',
        show: false,
    });
    const [rewriteModalOpen, setRewriteModalOpen] = useState(false);
    const [rewriteMarkedHtml, setRewriteMarkedHtml] = useState('');
    const defaultValue = getContentValue(item, !!r1_flag);
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    const { userSettings } = useAppSelector((state) => state.auth);
    const { editorSelectedOutput, targetCountries, conversationGroups } = useAppSelector(
        (state) => state.global,
    );
    const [editorValue, setEditorValue] = useState<string>(minifyHtml(defaultValue));
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [brands, setBrands] = useState<BrandType[] | []>([]);

    const dispatch = useAppDispatch();
    const [showElement, setShowElement] = useState(false);
    const { t } = useTranslation();
    const group = findGroup(conversationGroups, messageId);
    const countryCode = targetCountries.find((c) => c.title === country)?.country_code;
    const brandName = brands.find((b) => b.key === brand)?.name;
    const groupMessages = messages?.filter((m) => group?.messagesIds.includes(m.message_id));
    const isFirstGroupItem =
        groupMessages?.find((m: Message) => !m.isHidden)?.message_id === messageId;
    const isLastGroupItem =
        groupMessages?.filter((m: Message) => !m.isHidden).slice(-1)[0]?.message_id === messageId;
    const isThirdGroupItem =
        groupMessages &&
        groupMessages?.length > 3 &&
        groupMessages?.slice(2)[0].message_id === messageId;
    const isNotVisible = group?.messagesIds?.slice(3).includes(messageId);
    const groupBookmarksCount = groupMessages?.filter((m) => m.isBookmarked).length;
    const [isBiasOverlayVisible, setIsBiasOverlayVisible] = useState(
        is_bias_acknowledged ? false : has_bias,
    );

    useEffect(() => {
        const delay = isHidden ? 250 : 0;
        const timeoutId = setTimeout(() => {
            setShowElement(true);
        }, delay);
        return () => clearTimeout(timeoutId);
    }, [isHidden]);

    useEffect(() => {
        getBrands(setBrands);
    }, []);

    const onEditMode = () => {
        if (isFullScreenMode) {
            setIsEditMode((prev) => !prev);
        } else {
            const data = {
                version,
                answer: defaultValue,
                document_id,
            };
            dispatch(closeRightSidebarAutomatically());
            dispatch(setEditorSelectedOutput(messageId));
            dispatch(onOpenEditorFromOutput(data));
        }
    };

    const handleEditorChange = (value: string) => {
        setEditorValue(value);
    };

    const onOpenModal = () => {
        if (!isEmpty) {
            setIsFullScreen(true);
        }
    };

    const onCloseFullScreen = () => {
        setIsFullScreen(false);
    };

    const onSaveDraft = () => {
        try {
            if (!editorValue) {
                return;
            }
            const data = {
                version_id: document_id ?? null,
                user_id: auth?.currentUser?.uid,
                value: editorValue,
                created_at: new Date().toISOString(),
            };

            addFirestoreDocs(DRAFTS, data);
            onEditMode();
        } catch (error) {
            if (error instanceof Error) {
                errorLogger.critical(error?.message, 'onSaveDraft');
            }
            console.log(error);
        }
    };

    const handleCloseEditor = () => {
        setEditorValue(defaultValue);
        onEditMode();
    };

    const onCloseContext = () => {
        setContextMenu({ x: 0, y: 0, value: '', markedHtml: '', show: false });
    };
    const onCloseCopy = () => {
        setCopyMenu((prev) => ({ ...prev, value: '', show: false }));
    };

    const buildFullMarkedHtml = (data: SectionRewriteData) => {
        const selectedSectionIndex = R_ONE_FIELDS.indexOf(data.sectionKey as keyof Message);
        if (selectedSectionIndex === -1) {
            return data.selectedText;
        }

        const allContent: string[] = [];

        // Build content from all sections
        R_ONE_FIELDS.forEach((field, index) => {
            const fieldValue = item[field];
            if (typeof fieldValue === 'string') {
                if (index === selectedSectionIndex) {
                    // Use the pre-marked content from the section
                    allContent.push(data.sectionMarkedHtml);
                } else {
                    allContent.push(fieldValue);
                }
            }
        });

        return allContent.join('\n\n');
    };

    const handleOpenRewriteModal = (data: SectionRewriteData | string) => {
        onCloseContext();
        const markedHtml = typeof data === 'string' ? data : buildFullMarkedHtml(data);
        setRewriteMarkedHtml(markedHtml);
        setRewriteModalOpen(true);
    };

    const handleCloseRewriteModal = () => {
        setRewriteModalOpen(false);
        setRewriteMarkedHtml('');
    };

    const handleRewrite = async (data: { markedHtml: string; prompt: string }) => {
        handleRewriteFragment(data, messageId, group?.id);
        handleCloseRewriteModal();
    };

    const showTitle = () => {
        if (!title) {
            return isEditMode
                ? t('web_app.home.output.title_composing_pad')
                : t('web_app.home.output.title');
        }
        return title;
    };

    const handleCollapse = () => {
        const groupHiddenMessages = hiddenMessages.filter((id) => group?.messagesIds.includes(id));
        const updatedMessages = messages?.map((item) => {
            if (groupHiddenMessages.includes(item.message_id)) {
                return { ...item, isHidden: false };
            }
            return item;
        });
        const hiddenMessagesIds = hiddenMessages.filter((m) => !groupHiddenMessages.includes(m));

        const collapsedGroupsIds =
            conversationGroups?.filter((group) => group.isCollapsed).map((group) => group.id) || [];
        let updatedCollapsedGroupsIds = collapsedGroupsIds;
        if (group?.isCollapsed === false) {
            updatedCollapsedGroupsIds = [...collapsedGroupsIds, group.id];
        }
        if (group?.isCollapsed === true) {
            updatedCollapsedGroupsIds = collapsedGroupsIds?.filter((gId) => gId !== group.id);
        }

        if (document_id) {
            onUpdateDoc(CONVERSATIONS, document_id, {
                messages: updatedMessages,
                hiddenMessages: hiddenMessagesIds,
                collapsedGroups: updatedCollapsedGroupsIds,
            });
            dispatch(updateConversationGroup({ ...group, isCollapsed: !group?.isCollapsed }));
        }

        if (group && !group.isCollapsed && scrollToGroup) {
            scrollToGroup(group.id, false);
        }
    };

    const handleAcknowledge = (isAck: boolean) => {
        const updatedMessages = messages?.map((item) => {
            if (item.message_id == messageId) {
                item.is_bias_acknowledged = isAck;
            }
            return item;
        });

        if (document_id) {
            onUpdateDoc(CONVERSATIONS, document_id, {
                messages: updatedMessages,
            });
        }
    };

    return (
        <>
            {showElement && (
                <Box
                    className={`${
                        !isHidden && !isFullScreenMode && !isGeneratingForThis
                            ? 'animate__animated animate__pulse animate__faster animate__backwards'
                            : ''
                    } output-editor ${isEmpty ? 'output-editor-editional' : ''} ${
                        isHidden && !isFullScreenMode ? 'hidden' : ''
                    } ${isBookmarked && !isFullScreenMode ? 'bookmarked' : ''} ${
                        isFirstGroupItem ? 'first-group-item' : ''
                    } ${isLastGroupItem ? 'last-group-item' : ''} ${
                        group?.isCollapsed && !isFirstGroupItem ? 'collapse' : ''
                    } ${isThirdGroupItem ? 'third-group-item' : ''} ${
                        isNotVisible ? 'hidden-when-collapsed' : ''
                    }`}
                    sx={{
                        zIndex:
                            group && group.isCollapsed
                                ? -group?.messagesIds.indexOf(messageId)
                                : 'unset',
                        '&::before': {
                            background: group?.color + '40',
                        },
                    }}
                >
                    {group && !isLastGroupItem && !isHidden && (
                        <Box className="collapse-icon">
                            {group.isCollapsed ? (
                                <ArrowForward onClick={handleCollapse} />
                            ) : (
                                <ArrowBack onClick={handleCollapse} />
                            )}
                        </Box>
                    )}
                    {!isFullScreenMode && (
                        <Box className="title">
                            <Typography variant="h3" title={title}>
                                {showTitle()}
                            </Typography>
                            {!isEditMode && (
                                <Box className="title-description">
                                    <Box>
                                        {t('web_app.default_settings.represent_brand_title')}:{' '}
                                        {brandName ??
                                            t(
                                                'web_app.default_settings.represent_client_title',
                                            )}{' '}
                                    </Box>
                                    <Box>
                                        {t('web_app.home.output.country')}:{' '}
                                        {t(
                                            `web_app.settings.target_countries.${
                                                countryCode ?? userSettings?.target_country_code
                                            }`,
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                    {isEditMode ? (
                        <EditorWithFooter
                            value={editorValue}
                            handleEditorChange={handleEditorChange}
                            onSubmit={onSaveDraft}
                            isEditable
                            onCancel={handleCloseEditor}
                            title={`Edition version ${version}...`}
                            onCloseParentFullScreen={onCloseParentFullScreen}
                            isFullScreen={isFullScreenMode}
                            hasCancel
                            document_id={document_id}
                            messageId={messageId}
                        />
                    ) : (
                        <>
                            <Box
                                className={`quill-editor-custom ${
                                    isGeneratingForThis ? 'box-pulse-blue infinite' : ''
                                } ${editorSelectedOutput === messageId ? 'box-pulse-orange' : ''}`}
                                sx={{
                                    height: 'calc(100vh - 300px)',
                                }}
                            >
                                {!isHidden && has_bias && isBiasOverlayVisible && (
                                    <BiasOverlay
                                        isAck={!!is_bias_acknowledged}
                                        isVisible={isBiasOverlayVisible}
                                        hasBias={has_bias}
                                        biasReason={bias_reason ?? ''}
                                        onShowOutput={() => setIsBiasOverlayVisible(false)}
                                        onAck={handleAcknowledge}
                                    />
                                )}
                                <TopActions
                                    version={version}
                                    language={language}
                                    created_at={created_at}
                                    hasActionBtns={hasActionBtns}
                                    messagesLength={messagesLength}
                                    isHidden={isHidden}
                                    isBookmarked={isBookmarked}
                                    onCloseParentFullScreen={onCloseParentFullScreen}
                                    messages={messages}
                                    messageId={messageId}
                                    hiddenMessages={hiddenMessages}
                                    document_id={document_id ?? ''}
                                    isFullScreenMode={isFullScreenMode}
                                    onOpenModal={onOpenModal}
                                    isGeneratingForThis={!!isGeneratingForThis}
                                    group={group}
                                    isFirstGroupItem={isFirstGroupItem}
                                    groupBookmarksCount={groupBookmarksCount}
                                    showBiasTrigger={!!has_bias}
                                    onShowBiasTrigger={() => setIsBiasOverlayVisible(true)}
                                />
                                <Box className="outputed-text active-outputed-text">
                                    <>
                                        <Body
                                            defaultValue={defaultValue}
                                            streamingValue={streamingValue}
                                            isGeneratingForThis={isGeneratingForThis}
                                            setContextMenu={setContextMenu}
                                            setCopyMenu={setCopyMenu}
                                            contextMenuShow={contextMenu.show}
                                            isFullScreenMode={isFullScreenMode}
                                            copyMenu={copyMenu}
                                            isROne={r1_flag}
                                            disableContextMenu={r1_flag}
                                            ROneOutput={
                                                <SectionsOutput
                                                    item={item}
                                                    fields={R_ONE_FIELDS}
                                                    isGeneratingJob={!!isGeneratingForThis}
                                                    onSectionRewrite={handleOpenRewriteModal}
                                                />
                                            }
                                        />
                                        <Actions
                                            isGeneratingForThis={isGeneratingForThis}
                                            defaultValue={defaultValue}
                                            title={showTitle()}
                                            onEditMode={onEditMode}
                                        />
                                    </>
                                </Box>
                            </Box>
                            <RegenerateBtn
                                defaultValue={defaultValue}
                                isGeneratingJob={isGeneratingJob}
                                isGeneratingForThis={isGeneratingForThis}
                                stopGenerating={stopGenerating}
                                regenerate={regenerate}
                                onCloseParentFullScreen={onCloseParentFullScreen}
                                parameters={parameters}
                                language={language}
                                country={country}
                                isDoneGeneratingForThis={isDoneGeneratingForThis}
                                messageId={item.message_id}
                                brand={brand}
                                group={group}
                            />
                        </>
                    )}
                </Box>
            )}
            {isFullScreen && (
                <FullScreenModal
                    item={item}
                    open={isFullScreen}
                    onClose={onCloseFullScreen}
                    messages={messages}
                    isGeneratingJob={isGeneratingJob}
                    regenerate={regenerate}
                />
            )}
            {contextMenu.show && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    value={contextMenu.value}
                    onClose={onCloseContext}
                    onRewrite={() => handleOpenRewriteModal(contextMenu.markedHtml)}
                />
            )}
            {copyMenu.show && (
                <CopyView
                    x={copyMenu.x}
                    y={copyMenu.y}
                    value={copyMenu.value}
                    onClose={onCloseCopy}
                />
            )}
            <RewriteModal
                open={rewriteModalOpen}
                onClose={handleCloseRewriteModal}
                markedHtml={rewriteMarkedHtml}
                onRewrite={handleRewrite}
            />
        </>
    );
}

const MemoizedOutput = memo(Output, (prevProps, nextProps) => {
    if (prevProps.defaultValue !== nextProps.defaultValue) {
        return false;
    }
    if (prevProps.streamingValue !== nextProps.streamingValue) {
        return false;
    }
    if (prevProps.item.isHidden !== nextProps.item.isHidden) {
        return false;
    }
    if (prevProps.item.isBookmarked !== nextProps.item.isBookmarked) {
        return false;
    }
    if (prevProps.item.isLiked !== nextProps.item.isLiked) {
        return false;
    }
    if (prevProps.isGeneratingJob !== nextProps.isGeneratingJob) {
        return false;
    }
    if (prevProps.userSelectedCountry !== nextProps.userSelectedCountry) {
        return false;
    }
    if (prevProps.isGeneratingForThis !== nextProps.isGeneratingForThis) {
        return false;
    }
    if (prevProps.isDoneGeneratingForThis !== nextProps.isDoneGeneratingForThis) {
        return false;
    }
    if (prevProps.userSelectedLanguage !== nextProps.userSelectedLanguage) {
        return false;
    }
    if (prevProps.messagesLength !== nextProps.messagesLength) {
        return false;
    }
    if (prevProps.item?.content !== nextProps.item?.content) {
        return false;
    }
    if (
        prevProps.messages?.filter((m) => m.isBookmarked).length !==
        nextProps.messages?.filter((m) => m.isBookmarked).length
    ) {
        return false;
    }
    if (JSON.stringify(prevProps.hiddenMessages) !== JSON.stringify(nextProps.hiddenMessages)) {
        return false;
    }
    return true;
});

export default MemoizedOutput;
